import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { DRAWER_Z_INDEX } from '../../../definitions/constants';
import { useI18n } from '../../../lib/hook/useI18n';
import { getConsent } from '../../../lib/Consent';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';
import FormValidatorHelper from '../../../Services/FormValidatorService';
import PrivacyPolicyLink from '../../ConsentPromp/PrivacyPolicyLink';
import ThemableButton from '../../../theme/components/Button/ThemableButton';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';
import UnifiedContactIdentificationContent from './UnifiedContactIdentificationContent';
import UnifiedConsentText from './UnifiedConsentText';

interface Props {
  shouldUseUnifiedConsent?: boolean;
  isConsentDeclinedSuppression?: boolean;
  onCancel: () => void;
  onSubmit: (email: string) => void;
  open: boolean;
  saving: boolean;
}

const ContactIdentificationDialog: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const {
    shouldUseUnifiedConsent,
    isConsentDeclinedSuppression,
    onCancel,
    onSubmit,
    open,
    saving,
  } = props;
  const { i18nClasses, t, tc } = useI18n();

  const isInhouseConsentEnabled = useFeatureFlag(
    OrganizationFlagEnum.INHOUSE_CONSENT
  );

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const validator = new FormValidatorHelper();
  const isConsentAccepted = getConsent()?.consent === '1';

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.trim());
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    setSubmitted(true);

    if (!validator.isValid()) {
      return;
    }

    onSubmit(email);
  };

  const renderConsentText = () => {
    if (!isInhouseConsentEnabled || isConsentAccepted) {
      return;
    }
    if (!shouldUseUnifiedConsent) {
      return (
        <Grid item xs={12}>
          <Typography
            className={i18nClasses.root}
            color="textSecondary"
            variant="caption"
          >
            {tc(
              'room.identification.modal.consent',
              { actionText: t('room.contact.modal.continue') },
              {
                privacyPolicyLink: (
                  <PrivacyPolicyLink color="textSecondary" variant="caption" />
                ),
              },
              true
            )}
          </Typography>
        </Grid>
      );
    }

    return <UnifiedConsentText actionText={t('room.contact.modal.continue')} />;
  };

  const validatorKey = t('room.contact.modal.email').toLowerCase();
  validator.check(validatorKey, email, 'required|email');
  const error = validator.getError(validatorKey, submitted);

  // Use unified content when feature flag is enabled AND vendor is MindTickle
  if (shouldUseUnifiedConsent) {
    return (
      <UnifiedContactIdentificationContent
        consentDeclinedSuppressionUi={isConsentDeclinedSuppression}
        onCancel={onCancel}
        open={open}
        saving={saving}
        email={email}
        onEmailChange={onEmailChange}
        handleSubmit={handleSubmit}
        submitted={submitted}
      />
    );
  }

  // Return original contact identification content when feature flag is disabled
  return (
    <Dialog
      BackdropProps={{
        style: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'var(--dialog-background-color-dark)',
        },
      }}
      fullWidth
      maxWidth="xs"
      onClose={onCancel}
      open={open}
      PaperProps={{
        style: {
          borderRadius: '8px',
        },
      }}
      style={{ zIndex: DRAWER_Z_INDEX + 1 }}
    >
      <DialogTitle>{t('room.contact.modal.title')}</DialogTitle>
      <form onSubmit={handleSubmit} tabIndex={-1} noValidate>
        <DialogContent>
          <Box mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  color="secondary"
                  error={!!error}
                  fullWidth
                  helperText={error}
                  label={t('room.contact.modal.email')}
                  onChange={onEmailChange}
                  required
                  type="email"
                  value={email}
                  variant="outlined"
                />
              </Grid>
              {renderConsentText()}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <ThemableButton
            disabled={saving}
            onClick={onCancel}
            variant="outlined"
          >
            {t('room.contact.modal.cancel')}
          </ThemableButton>
          <ThemableButton disabled={saving} type="submit" variant="contained">
            {t('room.contact.modal.continue')}
          </ThemableButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactIdentificationDialog;
